import { useLazyQuery } from '@apollo/client/react';
import { useAtom } from 'jotai';
import { useEffect, useMemo } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { GetBookmarkPatientListDocument } from '~/graphql/generated/graphql';
import { getMe } from '~/graphql/utility';
import { bookmarkPatientListModalState } from '~/state/reception/atoms';

export const useBookmarkPatientList = () => {
  const [{ isOpen, yearMonth, page, perPage }, setPageInfo] = useAtom(
    bookmarkPatientListModalState,
  );
  const [getBookmarkPatientList, { data, loading }] = useLazyQuery(GetBookmarkPatientListDocument, {
    fetchPolicy: 'network-only',
  });

  // 連続して検索条件が変化した時に都度リクエストしないようにする
  const getBookmarkPatientListDebounce = useDebouncedCallback(async () => {
    await getBookmarkPatientList({
      variables: {
        month: yearMonth ? yearMonth.getMonth() + 1 : null,
        page: page,
        perPage: perPage,
        year: yearMonth ? yearMonth.getFullYear() : null,
        statuses: ['status1', 'status2', 'status3', 'status4', 'status5'], // "status6" "キャンセル"
        draftAppointmentStatuses: ['available', 'wait_for_booking', 'finished'],
      },
    });
  }, 300);

  const bookmarkPatientList = useMemo(
    () => (data ? getMe(data)?.organization.bookmarkedPatients.nodes || [] : []),
    [data],
  );

  const month = yearMonth ? yearMonth.getMonth() + 1 : null;
  const year = yearMonth ? yearMonth.getFullYear() : null;

  useEffect(() => {
    if (!data) return;
    const bookmarkedPatients = getMe(data)?.organization?.bookmarkedPatients;
    const bookmarkedPfPatientsCount = getMe(data)?.organization?.bookmarkedPfPatientsCount || 0;
    if (bookmarkedPatients) {
      const totalPage = bookmarkedPatients.pagesCount ?? 1;

      setPageInfo((state) => ({
        ...state,
        totalPage: totalPage,
        patientCount: bookmarkedPfPatientsCount,
      }));
    }
  }, [data, setPageInfo]);

  useEffect(() => {
    if (isOpen) {
      getBookmarkPatientListDebounce();
    }
  }, [isOpen, month, year, page, perPage, getBookmarkPatientListDebounce]);

  return { loading, bookmarkPatientList };
};
